import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { useQuery } from '@tanstack/react-query';
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import dayjs from 'dayjs';

import CustomRadioBox from '../../CustomRadioBox';
import LockerSelectModal from '../../SalesManagment/modal/enterSaleInfoModal/lockerSelectModal/LockerSelectModal';
import LockerAttachDateModal from '../../common/modal/LockerAttachDateModal';
import { getGeneralProducts } from '../../../api/user';
import { RadioRow, TransferHelperText } from './UserTransferStyled';

export const RECEIVER_SPOT = { KEEP: 'KEEP', OTHER: 'OTHER', NONE: 'NONE' };
export const INITIAL_LOCKER_TRANSFER = {
  passIds: [],
  receiverSpot: RECEIVER_SPOT.NONE,
  target: null,
  planEntries: null,
};

const today = () => dayjs().startOf('day');
const isExpired = lp => lp.status === 'INACTIVE' || (lp.expireDate && dayjs(lp.expireDate).isBefore(today(), 'day'));
const spotLabel = locker =>
  locker ? `${locker.lockerAreaName ? `${locker.lockerAreaName} ` : ''}${locker.lockerNumber}번` : '';

/**
 * 락커 양도 묶음 (#166) — 같은 자리에 붙은 양도자 이용권(사용 중·중지, 종료일 미경과)이 후보.
 * 자리 양도는 후보 전부를 넘겨야 하고 중지가 섞이면 고를 수 없다(서버 resolveLockerBundle 과 같은 기준).
 */
export const useLockerBundle = ({ open, product, senderMemberId, token, branchId, value, onChange }) => {
  const { data: senderProducts } = useQuery({
    queryKey: ['generalProducts', senderMemberId, branchId],
    queryFn: () => getGeneralProducts(senderMemberId, token, branchId),
    enabled: open && product?.productType === 'LOCKER' && !!senderMemberId && !!token && !!branchId,
  });
  const senderPasses = useMemo(() => senderProducts?.lockerPasses ?? [], [senderProducts]);
  const primary = senderPasses.find(lp => lp.memberLockerPassId === product?.productId);
  const spotId = primary?.branchLockerId ?? null;

  const candidates = useMemo(() => {
    if (!primary) return [];
    if (!spotId) return [primary];
    return senderPasses
      .filter(lp => lp.branchLockerId === spotId && ['ACTIVE', 'STOPPED'].includes(lp.status) && !isExpired(lp))
      .sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));
  }, [senderPasses, primary, spotId]);
  const hasStopped = candidates.some(lp => lp.status === 'STOPPED');
  const allIds = candidates.filter(lp => lp.status !== 'STOPPED').map(lp => lp.memberLockerPassId);
  const keepBlocked = !spotId || hasStopped;

  // 기본값 — 미배정 + 고른 이용권 하나만 (사용자 결정 2026-10-13)
  useEffect(() => {
    if (!open || !primary) return;
    onChange({ ...INITIAL_LOCKER_TRANSFER, passIds: [primary.memberLockerPassId] });
    setNeedAll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, primary?.memberLockerPassId]);

  // 자리 양도는 이 자리 이용권을 전부 고른 뒤에만 고를 수 있다. 덜 골랐으면 목록으로 포커스를 옮겨 나머지를 고르게 한다.
  const listRef = useRef(null);
  const [needAll, setNeedAll] = useState(false);
  const allSelected = allIds.every(id => value.passIds.includes(id));
  useEffect(() => {
    if (allSelected) setNeedAll(false);
  }, [allSelected]);

  const requestKeep = () => {
    if (allSelected) {
      onChange({ ...value, receiverSpot: RECEIVER_SPOT.KEEP, passIds: allIds, target: null, planEntries: null });
      return;
    }
    setNeedAll(true);
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const firstUnchecked = listRef.current?.querySelector('input[type="checkbox"]:not(:checked):not(:disabled)');
    firstUnchecked?.focus();
  };

  return {
    senderPasses,
    primary,
    spotId,
    candidates,
    hasStopped,
    allIds,
    keepBlocked,
    value,
    onChange,
    listRef,
    needAll,
    requestKeep,
  };
};

/** 양도할 이용권 — 같은 자리에 여러 개면 체크 목록, 하나면 카드 한 장. */
export const LockerPassPicker = ({ bundle, product, renderSingle }) => {
  const { t } = useTranslation();
  const { primary, candidates, value, onChange, listRef, needAll } = bundle;
  if (!primary || candidates.length <= 1) return renderSingle();

  const isKeep = value.receiverSpot === RECEIVER_SPOT.KEEP;
  const togglePass = id => {
    const passIds = value.passIds.includes(id) ? value.passIds.filter(x => x !== id) : [...value.passIds, id];
    // 자리 양도 중에 하나라도 빼면 자리 양도가 성립하지 않으므로 미배정으로 돌린다.
    // 넘길 이용권이 바뀌면 합치기 배치는 다시 해야 한다.
    onChange({
      ...value,
      passIds,
      ...(isKeep && { receiverSpot: RECEIVER_SPOT.NONE }),
      planEntries: null,
      target: value.planEntries ? null : value.target,
    });
  };

  return (
    <>
      <Box
        ref={listRef}
        sx={{
          border: needAll ? '1.5px solid #B0573E' : '1px solid #E5E5E5',
          boxShadow: needAll ? '0 0 0 3px rgba(176,87,62,0.12)' : 'none',
          borderRadius: '8px',
          overflow: 'hidden',
          transition: 'border-color .2s, box-shadow .2s',
        }}>
        <Box
          sx={{
            p: '10px 14px',
            background: '#FAFAFA',
            borderBottom: '1px solid #F0F0F0',
            fontSize: 13,
            color: '#595959',
          }}>
          {product.lockerSpot} · {t('이 회원 이용권')} {candidates.length}
          {t('개')}
        </Box>
        {candidates.map(lp => {
          const stopped = lp.status === 'STOPPED';
          const isPrimary = lp.memberLockerPassId === primary.memberLockerPassId;
          return (
            <FormControlLabel
              key={lp.memberLockerPassId}
              disabled={isPrimary || stopped}
              sx={{
                display: 'flex',
                m: 0,
                p: '4px 8px',
                borderBottom: '1px solid #F5F5F5',
                '&:last-of-type': { borderBottom: 'none' },
              }}
              control={
                <Checkbox
                  size="small"
                  checked={value.passIds.includes(lp.memberLockerPassId)}
                  onChange={() => togglePass(lp.memberLockerPassId)}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: 13 }}>
                  <Box component="span" sx={{ fontWeight: 600, color: stopped ? '#A6A6A6' : '#141414' }}>
                    {lp.productName}
                  </Box>
                  <Box component="span" sx={{ color: stopped ? '#A6A6A6' : '#595959' }}>
                    {lp.startDate} ~ {lp.expireDate}
                  </Box>
                  {isPrimary && (
                    <Box component="span" sx={{ fontSize: 12, color: '#1F3A5F', fontWeight: 600 }}>
                      {t('선택한 이용권')}
                    </Box>
                  )}
                  {stopped && (
                    <Box component="span" sx={{ fontSize: 12, color: '#B0573E' }}>
                      {t('중지 중 · 양도 불가')}
                    </Box>
                  )}
                </Box>
              }
            />
          );
        })}
      </Box>
      {needAll && (
        <TransferHelperText sx={{ color: '#B0573E' }}>
          {t('자리 양도는 이 자리 이용권을 모두 선택해야 합니다. 나머지 이용권을 선택한 뒤 다시 고르세요.')}
        </TransferHelperText>
      )}
      {isKeep && <TransferHelperText>{t('자리 양도는 이 자리 이용권을 모두 넘깁니다.')}</TransferHelperText>}
    </>
  );
};

/** 양수자 라커 자리 — 자리 양도 / 다른 자리 배정 / 미배정. */
export const LockerReceiverSpotPicker = ({ bundle, product, receiver, token, branchId, open }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [selectOpen, setSelectOpen] = useState(false);
  const [attachTarget, setAttachTarget] = useState(null);
  const { senderPasses, primary, spotId, candidates, hasStopped, keepBlocked, value, onChange, requestKeep } = bundle;

  const receiverMemberId = receiver && receiver.userType !== 'T' ? receiver.memberId : null;
  const { data: receiverProducts } = useQuery({
    queryKey: ['generalProducts', receiverMemberId, branchId],
    queryFn: () => getGeneralProducts(receiverMemberId, token, branchId),
    enabled: open && !!receiverMemberId && !!token && !!branchId,
  });

  // 양수자가 바뀌면 고른 자리·기간 배치는 무효
  useEffect(() => {
    if (value.target) onChange({ ...value, target: null, planEntries: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiverMemberId]);

  if (!primary) return null;

  const setMode = mode => {
    if (mode === RECEIVER_SPOT.KEEP) {
      requestKeep();
      return;
    }
    onChange({
      ...value,
      receiverSpot: mode,
      ...(mode !== RECEIVER_SPOT.OTHER && { target: null, planEntries: null }),
    });
  };

  const movingPasses = () =>
    senderPasses
      .filter(lp => value.passIds.includes(lp.memberLockerPassId))
      // 양수자 새 이용권 시작일 = 오늘과 원래 시작일 중 늦은 날(서버와 같은 규칙)
      .map(lp => ({
        ...lp,
        startDate: dayjs(lp.startDate).isBefore(today()) ? today().format('YYYY-MM-DD') : lp.startDate,
      }));

  const openSpotPicker = () => {
    if (!receiverMemberId) {
      toast.error(t('양수자를 먼저 선택해주세요.'));
      return;
    }
    setSelectOpen(true);
  };

  const handleSpotSelected = locker => {
    if (locker.lockerId === spotId) {
      toast.error(t("원래 자리로 넘기려면 '자리 양도'를 고르세요."));
      return false;
    }
    setSelectOpen(false);
    if (locker.memberId === receiverMemberId) {
      // 양수자 본인이 쓰는 자리 — 합치기 창에서 기간을 배치한다
      setAttachTarget(locker);
      return undefined;
    }
    onChange({ ...value, target: locker, planEntries: null });
    return undefined;
  };

  const confirmAttach = entries => {
    onChange({
      ...value,
      target: attachTarget,
      planEntries: entries.map(e => ({
        memberLockerPassId: e.memberLockerPassId,
        startDate: dayjs(e.startDate).format('YYYY-MM-DD'),
        expireDate: dayjs(e.expireDate).format('YYYY-MM-DD'),
      })),
    });
    setAttachTarget(null);
  };

  const isOther = value.receiverSpot === RECEIVER_SPOT.OTHER;

  return (
    <>
      <RadioRow sx={{ minHeight: '44px', flexWrap: 'wrap', rowGap: 0 }}>
        {spotId && (
          <FormControlLabel
            disabled={keepBlocked}
            control={
              <CustomRadioBox
                checked={value.receiverSpot === RECEIVER_SPOT.KEEP}
                onChange={() => setMode(RECEIVER_SPOT.KEEP)}
              />
            }
            label={`${t('자리 양도')} (${product.lockerSpot})`}
          />
        )}
        <FormControlLabel
          control={<CustomRadioBox checked={isOther} onChange={() => setMode(RECEIVER_SPOT.OTHER)} />}
          label={t('다른 자리 배정')}
        />
        <FormControlLabel
          control={
            <CustomRadioBox
              checked={value.receiverSpot === RECEIVER_SPOT.NONE}
              onChange={() => setMode(RECEIVER_SPOT.NONE)}
            />
          }
          label={t('미배정')}
        />
      </RadioRow>

      {isOther && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            mt: '8px',
            p: '10px 14px',
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
          }}>
          <Typography sx={{ flex: 1, fontSize: 13, color: value.target ? '#141414' : '#A6A6A6', fontWeight: 600 }}>
            {value.target
              ? `${spotLabel(value.target)}${value.planEntries ? ` · ${t('기존 이용권과 기간 배치함')}` : ''}`
              : t('배정할 자리를 선택하세요')}
          </Typography>
          <Button size="small" variant="outlined" onClick={openSpotPicker} sx={{ height: 32, whiteSpace: 'nowrap' }}>
            {value.target ? t('자리 변경') : t('자리 선택')}
          </Button>
        </Box>
      )}

      {hasStopped && spotId && (
        <TransferHelperText>
          {t(
            "이 자리에 중지된 이용권이 있어 자리 양도는 할 수 없습니다. 중지를 해제하거나 '다른 자리 배정'·'미배정'을 고르세요.",
          )}
        </TransferHelperText>
      )}
      {!hasStopped && candidates.length > 1 && value.receiverSpot !== RECEIVER_SPOT.KEEP && (
        <TransferHelperText>
          {t('자리 양도는 양도할 이용권에서 이 자리 이용권을 모두 선택해야 고를 수 있습니다.')}
        </TransferHelperText>
      )}

      <LockerSelectModal
        open={selectOpen}
        onClose={() => setSelectOpen(false)}
        onSelect={handleSpotSelected}
        memberId={receiverMemberId}
        initialLocker={spotId ? { lockerId: spotId } : null}
        title={t('양수자 라커 자리')}
        confirmText={t('선택')}
      />
      <LockerAttachDateModal
        open={!!attachTarget}
        onClose={() => setAttachTarget(null)}
        onConfirm={confirmAttach}
        lockerNumber={attachTarget?.lockerNumber}
        spotPasses={(receiverProducts?.lockerPasses ?? [])
          .filter(
            lp => lp.branchLockerId === attachTarget?.lockerId && ['ACTIVE', 'STOPPED', 'INACTIVE'].includes(lp.status),
          )
          .map(lp => ({ ...lp, expired: isExpired(lp) }))}
        movingPasses={attachTarget ? movingPasses() : []}
      />
    </>
  );
};
